import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Scene } from '@babylonjs/core/scene';
import { BaseConnector } from '@models/classes/features';
import { Connector, isConnector } from '@models/classes/features/connector';
import { MateNode } from '@models/classes/mates/mate-node';
import { isSketchControlPoint } from '@models/classes/meshes/mesh-utilities';
import { SpaceNode } from '@models/classes/meshes/space-node';
import { KbQuaternion } from '@models/classes/primitives/kb-quaternion';
import { KbVector } from '@models/classes/primitives/kb-vector';
import { Token } from '@models/classes/token';
import { ITrackedClass } from '@models/interfaces/tracked-class';
import { mateConnectors, mateControlPointToConnector } from '@view/helpers/mate-helper';
import { KbViewer } from '@view/kb-viewer';
import { ConnectorRenderer } from '../features/connector';
import { SketchControlPointRenderer } from '../features/sketch-control-point';
import { ChangeMap } from '../renderer-manager';
import { sortMates } from './mate-sorter';
import { Processor } from './processor';
import { ReferenceProcessor } from './reference-processor';
import { eConnectorPositionMode } from '@models/classes';

const CYCLE_ERROR_START = 'Cycle detected:  ';

export class MateProcessor extends Processor {
    /**
     * Mates sorted based on their dependencies of each other
     */
    private sortedMates: Set<MateNode>;
    /**
     * If during rebuilding of the sorted mate graph any cycles are detected, they are stored here
     */
    private cycles: MateNode[][];

    /**
     * we try to avoid rebuilding the mate dependency graph unless a change warrants it.
     */
    private rebuildDependencies: boolean;

    constructor(
        protected viewer: KbViewer,
        protected scene: Scene,
        protected connectorRenderer: ConnectorRenderer,
        protected controlPointRenderer: SketchControlPointRenderer,
        protected referenceProcessor: ReferenceProcessor
    ) {
        super();
    }

    beforeRender() {
        this.rebuildDependencies = false;
    }

    beforeDeletions(deletions: ChangeMap) {
        const mateDeletions = deletions.get(Token.MateNode) as Array<ITrackedClass<MateNode>>;
        const connectorDeletions = deletions.get(Token.Connector) as Array<ITrackedClass<Connector>>;
        this.rebuildDependencies =
            this.rebuildDependencies || mateDeletions.length > 0 || connectorDeletions.length > 0;
    }

    beforeUpdates(changes: ChangeMap) {
        //determine if mate dependencies need to be rebuilt
        const mateChanges = changes.get(Token.MateNode) as Array<ITrackedClass<MateNode>>;
        const connectorChanges = changes.get(Token.Connector) as Array<ITrackedClass<Connector>>;
        const meshChanges = changes.get(Token.SpaceNode) as Array<ITrackedClass<SpaceNode>>;

        this.rebuildDependencies =
            this.rebuildDependencies ||
            mateChanges.some(m => m.changes.hasAny('connector1', 'connector2', 'enabled')) ||
            connectorChanges.some(c => c.changes.has('enabled')) ||
            meshChanges.some(c => c.changes.hasAny('fixed', '_primeMover'));

        /**
         * Mates need to be reprocessed if:
         * * the mate itself was changed
         * * connector1 of the node was changed
         * * parent node of connector1 was changed
         * * any ancestor space node of the parent node was changed
         * * any parent graph dependency node mate or it's parents were changed
         */
    }

    afterUpdates(changes: ChangeMap) {
        if (this.rebuildDependencies) {
            this.buildDependencies();
        }
        if (this.sortedMates) {
            if (
                changes.has(Token.MateNode) ||
                changes.has(Token.SpaceNode) ||
                changes.has(Token.Connector) ||
                changes.has(Token.Feature)
            ) {
                for (const mate of this.sortedMates) {
                    //don't process the last mate in a cycle
                    if (!this.cycles || !this.cycles.some(c => c.last() === mate)) {
                        this.processMate(mate);
                    }
                }
            }
        }
    }

    dispose() {
        if (this.sortedMates) this.sortedMates.clear();
        if (this.cycles) this.cycles.clear();
    }

    processMate(mate: MateNode) {
        let c1 = mate.getC1()!;
        let c2 = mate.getC2()!;

        /** 
         * If the sorter determined we need to swap the connectors. 
         * */
        if (mate._swap || c2.positionMode === eConnectorPositionMode.moveAlongSketch) {
            const temp = c1;
            c1 = c2;
            c2 = temp;
        }

        const c1Mesh = this.getConnectorMesh(c1);
        const c2Mesh = this.getConnectorMesh(c2);
        const followerNode = c2._parent! as ITrackedClass<SpaceNode>;
        const followerMesh = this.viewer.rendererManager.mesh.get3d(followerNode) as TransformNode;

        if (!c1Mesh || !c2Mesh) return;

        /** 
         * Move along sketch connectors cannot be moved, because their position is being set by the sketch itself. They can only be used 
         * to determine the position of other connectors. Moving them will cause an immediate overconstraint condition
         */
        if(c2.positionMode === eConnectorPositionMode.moveAlongSketch) {
            return;
        }

        if (isSketchControlPoint(c2)) {
            const cp = c2;
            this.viewer.pauseRenderWhile(() => {
                const nodeWithTransform = mateControlPointToConnector(mate, c1Mesh, c2Mesh);
                const d1 = nodeWithTransform.direction.scale(cp.direction.length())
                const d2 = cp.direction2 ? nodeWithTransform.direction.scale(cp.direction2.length()) : undefined;
                if(
                    !nodeWithTransform.position.equalsWithEpsilon(cp.position.toVec3()) || 
                    !d1.equalsWithEpsilon(cp.direction.toVec3()) || 
                    (cp.direction2 && d2 && !d2.equalsWithEpsilon(cp.direction2.toVec3()))
                ) {
                    this.afterCleanup(() => {
                        cp.position = KbVector.FromVec3(nodeWithTransform.position);
                        cp.direction = KbVector.FromVec3(d1);
                        if(cp.direction2 && d2) {
                            cp.direction2 = KbVector.FromVec3(d2);
                        }
                    });
                }
            });
        } else if (followerMesh) {
            this.viewer.pauseRenderWhile(() => {
                const originalFollowerMeshAbsPos = followerMesh.getAbsolutePosition();
                const originalFollowerMeshAbsQuat = followerMesh.absoluteRotationQuaternion.clone();
                const originalFollowerMeshLocalPos = followerMesh.position.clone();
                const originalFollowerMeshLocalQuat = followerMesh.rotationQuaternion!.clone();

                mateConnectors(mate, followerMesh, c1Mesh, c2Mesh);

                /** update real kb3d nodes with new transform info */
                if (
                    !originalFollowerMeshLocalPos.equalsWithEpsilon(followerMesh.position) ||
                    !originalFollowerMeshLocalQuat.equalsWithEpsilon(followerMesh.rotationQuaternion!)
                ) {
                    followerNode.trackChanges(false);
                    followerNode.setRotationQuaternion(KbQuaternion.FromQuat(followerMesh.rotationQuaternion!));
                    followerNode.position = KbVector.FromVec3(followerMesh.position);
                    followerNode.trackChanges(true);
                    this.referenceProcessor.triggerChangesOnReferencers(followerNode, new Map([['position', null]]));
                }

                /** invalidate bounding box of parent if follower mesh actually moved */
                if (
                    !originalFollowerMeshAbsPos.equalsWithEpsilon(followerMesh.getAbsolutePosition()) ||
                    !originalFollowerMeshAbsQuat.equalsWithEpsilon(followerMesh.absoluteRotationQuaternion)
                ) {
                    this.viewer.bbCache.invalidate(c2.getSpaceParent());
                }

                //console.log(`mate '${mate.name} processed`);
            });
        }
    }

    protected getConnectorMesh(c: BaseConnector) {
        if (isSketchControlPoint(c)) {
            return this.controlPointRenderer.get3d(c)!;
        } else if (isConnector(c)) {
            return this.connectorRenderer.get3d(c)!;
        }
    }

    protected buildDependencies() {
        const mates = this.getValidMates();
        const sortResult = sortMates(mates);
        this.sortedMates = sortResult.sorted;
        this.cycles = sortResult.cycles;

        const validator = this.viewer.options.validator;
        for (const cycle of sortResult.cycles) {
            const err = `${CYCLE_ERROR_START}${cycle.map(n => n.name).join(' -> ')}`;
            if (validator) {
                for (const cm of cycle) {
                    validator.addError(cm, '', err);
                }
            } else {
                console.warn(err);
            }
        }
        if (validator) {
            for (const om of sortResult.overConstrained) {
                validator.addError(om, '', `Cannot have more than one fixed node in the mate chain`);
            }
        } else {
            if (sortResult.overConstrained.length) {
                console.warn(`Mates overconstrained: [${sortResult.overConstrained.map(n => n.name).join(', ')}]`);
            }
        }
    }

    /**
     * gets all mates (including those in nested scenes)
     * that are enabled, and have valid references to connectors
     */
    protected getValidMates() {
        const rootScene = this.viewer.sceneNode;
        const mates = new Array<MateNode>();
        const validator = this.viewer.options.validator;

        rootScene.runOnAllScenes(s => {
            const sceneMates = s._manager!.getByToken(Token.MateNode);
            for (const m of sceneMates) {
                if (m.isActive()) {
                    mates.push(m);
                }

                //regardless, clear out cycle validation
                if (validator) {
                    validator.clearProp(m, '');
                }
            }
        });
        return mates;
    }
}
